import React from "react";
import { Alert, Box, Stack, Typography } from "@material-ui/core";
import type { AlertProps } from "@material-ui/lab/Alert";

/**
 * MUI's `<Alert>` lays its root out as `display: flex` with the icon, the
 * message slot, and the (optional) `action` node living side-by-side. That
 * works for one-line alerts but breaks down the moment we want a real body:
 *
 *  - title / body / action all collapse onto the same horizontal row
 *  - the action prop can never appear under the body, only beside it
 *  - block children get pulled toward the centre by the root's
 *    `align-items: center`
 *
 * We hit this enough times across the app (signatures send form, template
 * editor missing-fields warning, etc.) that the right answer is one shared
 * component instead of repeating the same `sx` overrides every time.
 *
 * Layout produced:
 *
 *   ┌─────────────────────────────────────┐
 *   │ ⚠  title                            │
 *   │    body / list / explainer          │
 *   │    [ action button ]                │
 *   └─────────────────────────────────────┘
 *
 * Use this for any Alert that has more than a single short line.
 */
type StackedAlertProps = Omit<AlertProps, "action" | "title"> & {
  /** Optional title rendered as a subtitle above the body. */
  title?: React.ReactNode;
  /**
   * Optional CTA / action element rendered on its own row below the body,
   * left-flush (LTR) / right-flush (RTL). Pass null/undefined to skip.
   */
  action?: React.ReactNode;
  children?: React.ReactNode;
};

export default function StackedAlert({
  title,
  action,
  children,
  sx,
  ...alertProps
}: StackedAlertProps) {
  // Render the title as Typography only when a string is passed; ReactNode
  // titles are passed through verbatim so callers can use AlertTitle or
  // anything else they like.
  const titleNode =
    typeof title === "string" ? (
      <Typography variant="subtitle2">{title}</Typography>
    ) : (
      title
    );

  return (
    <Alert
      {...alertProps}
      sx={{
        // Make the message slot fill the row and align its content to the
        // start (left in LTR, right in RTL). Without this, single-line
        // children get pulled toward the horizontal centre.
        "& .MuiAlert-message": {
          width: "100%",
          textAlign: "start"
        },
        ...((sx as object) || {})
      }}
    >
      <Stack spacing={1} alignItems="stretch">
        {titleNode}
        {children}
        {action && <Box sx={{ alignSelf: "flex-start" }}>{action}</Box>}
      </Stack>
    </Alert>
  );
}
